import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';
import {
  ShoppingBag, Plus, Minus, X, ArrowLeft, ArrowRight,
  Package, CheckCircle, Loader2, Search, MapPin, Phone,
  Trash2, AlertCircle, ChevronDown,
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// ── Design tokens ─────────────────────────────────────────────────────────────
const DEFAULT_T = {
  dark:   '#0E0D0B',
  gold:   '#B08D57',
  goldBg: 'rgba(176,141,87,0.10)',
  goldBd: 'rgba(176,141,87,0.22)',
  bg:     '#FAFAF8',
  beige:  '#EDE9E3',
  card:   '#FFFFFF',
  muted:  '#6B6460',
  subtle: 'rgba(14,13,11,0.08)',
};

const ThemeContext = React.createContext(DEFAULT_T);

const serif = `Georgia, 'Times New Roman', serif`;
const sans  = `-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

// label: static type treatment - color must be provided at each usage site
const label = {
  fontFamily: sans,
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
};

const hex2rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// ─── Page principale ──────────────────────────────────────────────────────────

export default function ShopPage() {
  const { slug }                    = useParams();
  const [salon, setSalon]           = useState(null);
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [view, setView]             = useState('shop');
  const [cart, setCart]             = useState([]);
  const [showCart, setShowCart]     = useState(false);
  const [lastTotal, setLastTotal]   = useState(0);
  const [filterCat, setFilterCat]       = useState('');
  const [search, setSearch]             = useState('');
  const [scrolled, setScrolled]         = useState(false);
  const [selectedProduct, setSelected] = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    axios.get(`${BASE_URL}/api/shop/${slug}`)
      .then(r => { setSalon(r.data.data.salon); setProducts(r.data.data.products); setCategories(r.data.data.categories); })
      .catch(e => setError(e.response?.data?.message || 'Boutique introuvable.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.productId === product._id);
      if (idx !== -1) {
        if (prev[idx].quantite >= product.stock) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + 1 };
        return next;
      }
      return [...prev, { productId: product._id, nom: product.nom, prix: product.prix, image: product.image, quantite: 1, stock: product.stock }];
    });
  }, []);

  const updateQty      = useCallback((id, d) => setCart(prev => prev.map(i => i.productId === id ? { ...i, quantite: i.quantite + d } : i).filter(i => i.quantite > 0)), []);
  const removeFromCart = useCallback((id) => setCart(prev => prev.filter(i => i.productId !== id)), []);

  const total     = cart.reduce((s, i) => s + i.prix * i.quantite, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantite, 0);
  const visible   = products.filter(p =>
    (!filterCat || p.categorie === filterCat) &&
    (!search    || p.nom.toLowerCase().includes(search.toLowerCase()))
  );

  // Derive theme from salon customization data
  const T = useMemo(() => {
    const gold = salon?.shopAccentColor || DEFAULT_T.gold;
    const dark = salon?.shopDarkColor   || DEFAULT_T.dark;
    if (gold === DEFAULT_T.gold && dark === DEFAULT_T.dark) return DEFAULT_T;
    return {
      ...DEFAULT_T,
      gold,
      dark,
      goldBg: hex2rgba(gold, 0.10),
      goldBd: hex2rgba(gold, 0.22),
      subtle: hex2rgba(dark, 0.08),
    };
  }, [salon?.shopAccentColor, salon?.shopDarkColor]);

  if (loading) return <Loader />;
  if (error)   return <ErrScreen msg={error} />;

  return (
    <ThemeContext.Provider value={T}>
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans }}>

      {/* ━━━ HEADER ━━━ */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        height: '60px',
        display: 'flex', alignItems: 'center',
        padding: '0 32px',
        background: scrolled ? 'rgba(250,250,248,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${T.subtle}` : '1px solid transparent',
        transition: 'all 0.4s ease',
      }}>
        {/* Identité */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ ...label, color: scrolled ? T.gold : 'rgba(176,141,87,0.7)' }}>Boutique</span>
            <span style={{ fontFamily: sans, fontWeight: 800, fontSize: '13px', color: scrolled ? T.dark : '#fff', letterSpacing: '-0.02em', marginTop: '1px' }}>
              {salon?.nom}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {salon?.telephone && (
            <a href={`tel:${salon.telephone}`} style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '8px 16px', borderRadius: '100px',
              fontFamily: sans, fontSize: '12px', fontWeight: 700,
              color: scrolled ? T.gold : 'rgba(176,141,87,0.9)',
              border: `1px solid ${scrolled ? T.goldBd : 'rgba(176,141,87,0.3)'}`,
              background: scrolled ? T.goldBg : 'rgba(176,141,87,0.08)',
              textDecoration: 'none', transition: 'all 0.3s ease',
            }}>
              <Phone size={12} /> {salon.telephone}
            </a>
          )}
          <button
            onClick={() => setShowCart(true)}
            style={{
              position: 'relative', display: 'flex', alignItems: 'center', gap: '8px',
              padding: '9px 20px', borderRadius: '100px',
              background: scrolled ? T.dark : 'rgba(14,13,11,0.6)',
              backdropFilter: !scrolled ? 'blur(10px)' : 'none',
              border: 'none', cursor: 'pointer',
              fontFamily: sans, fontSize: '12px', fontWeight: 700, color: '#fff',
              transition: 'all 0.3s ease',
            }}
          >
            <ShoppingBag size={13} />
            <span>Panier</span>
            {cartCount > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                width: '18px', height: '18px', borderRadius: '50%',
                background: T.gold, color: '#fff',
                fontSize: '9px', fontWeight: 900,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{cartCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* ━━━ VUE BOUTIQUE ━━━ */}
      {view === 'shop' && (
        <div style={{ paddingBottom: cartCount > 0 ? '90px' : 0 }}>

          {/* ── HERO ── */}
          <section style={{
            background: T.dark,
            backgroundImage: salon?.shopHeroImage ? `url(${salon.shopHeroImage})` : 'none',
            backgroundSize: 'cover', backgroundPosition: 'center',
            minHeight: salon?.shopHeroImage ? '85vh' : '78vh',
            position: 'relative', overflow: 'hidden',
          }}>
            {salon?.shopHeroImage && (
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 100%)', zIndex: 0 }} />
            )}

            {/* Texture grain subtil */}
            {!salon?.shopHeroImage && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 0,
                backgroundImage: `radial-gradient(circle at 20% 80%, ${hex2rgba(T.gold, 0.07)} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${hex2rgba(T.gold, 0.05)} 0%, transparent 40%)`,
              }} />
            )}

            {/* Ligne gold en haut */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1.5px', background: `linear-gradient(90deg, transparent 0%, ${T.gold} 40%, ${T.gold} 60%, transparent 100%)`, zIndex: 2 }} />

            {/* Fade bas vers la page */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '160px', background: `linear-gradient(to bottom, transparent, ${T.bg})`, pointerEvents: 'none', zIndex: 2 }} />

            {/* Contenu centré */}
            <div style={{
              position: 'relative', zIndex: 3,
              display: 'flex', flexDirection: 'column',
              justifyContent: 'center', alignItems: 'center', textAlign: 'center',
              padding: '120px 24px 100px',
              minHeight: salon?.shopHeroImage ? '85vh' : '78vh',
            }}>
              {/* Tagline décoratif */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
                <div style={{ height: '1px', width: '40px', background: hex2rgba(T.gold, 0.4) }} />
                <span style={{ ...label, color: T.gold, letterSpacing: '0.45em' }}>
                  {salon?.shopTagline || 'Beauté · Soins · Prestige'}
                </span>
                <div style={{ height: '1px', width: '40px', background: hex2rgba(T.gold, 0.4) }} />
              </div>

              {/* Nom en serif italique */}
              <h1 style={{
                fontFamily: serif, fontStyle: 'italic', fontWeight: 400,
                fontSize: 'clamp(3.2rem, 10vw, 7.5rem)',
                color: '#fff', lineHeight: 1.05, letterSpacing: '-0.01em',
                marginBottom: '24px', textShadow: '0 2px 40px rgba(0,0,0,0.4)',
              }}>
                {salon?.nom}
              </h1>

              {/* Rule sous le nom */}
              <div style={{ width: '48px', height: '1px', background: T.gold, marginBottom: '28px', opacity: 0.6 }} />

              <p style={{ fontFamily: sans, fontWeight: 400, fontSize: '15px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.8, maxWidth: '400px', marginBottom: '44px', letterSpacing: '0.01em' }}>
                Découvrez notre collection de produits soigneusement sélectionnés pour sublimer votre beauté.
              </p>

              {/* CTAs */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                {salon?.telephone && (
                  <a href={`tel:${salon.telephone}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '10px',
                    padding: '15px 32px', borderRadius: '100px',
                    background: T.gold, color: '#fff', textDecoration: 'none',
                    fontFamily: sans, fontWeight: 800, fontSize: '14px',
                    boxShadow: `0 8px 40px ${hex2rgba(T.gold, 0.30)}`,
                    letterSpacing: '0.01em',
                  }}>
                    <Phone size={15} /> {salon.telephone}
                  </a>
                )}
                {salon?.adresse && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: sans, fontSize: '13px', color: 'rgba(255,255,255,0.28)', fontWeight: 500 }}>
                    <MapPin size={12} /> {salon.adresse}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* ── FILTRES ── */}
          <style>{`
            .shop-search-input { font-size: 14px !important; }
            @media (max-width: 640px) { .shop-search-input { font-size: 16px !important; } }
            .shop-cat-pill { transition: all 0.18s ease; }
            .shop-cat-pill:hover { transform: translateY(-1px); }
            .shop-grid {
              display: grid;
              gap: 8px;
              grid-template-columns: repeat(2, 1fr);
            }
            @media (min-width: 600px) {
              .shop-grid { grid-template-columns: repeat(3, 1fr); gap: 12px; }
            }
            @media (min-width: 1024px) {
              .shop-grid { grid-template-columns: repeat(4, 1fr); gap: 16px; }
            }
            @media (max-width: 599px) {
              .shop-filter-search { max-width: 100% !important; }
            }
          `}</style>
          <div style={{
            position: 'sticky', top: '60px', zIndex: 40,
            background: 'rgba(250,250,248,0.96)',
            backdropFilter: 'blur(24px)',
            borderBottom: `1px solid ${hex2rgba(T.gold, 0.12)}`,
          }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '14px 20px 0' }}>

              {/* Recherche pleine largeur */}
              <div style={{ position: 'relative', maxWidth: '480px' }} className="shop-filter-search">
                <Search size={13} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: T.gold, pointerEvents: 'none' }} />
                <input
                  className="shop-search-input"
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un produit…"
                  style={{
                    width: '100%', padding: '11px 14px 11px 40px',
                    fontFamily: sans, fontWeight: 500, fontSize: '14px', color: T.dark,
                    background: T.card, border: `1.5px solid ${hex2rgba(T.gold, 0.18)}`,
                    borderRadius: '12px', outline: 'none', transition: 'border-color 0.2s',
                    boxSizing: 'border-box', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                  onFocus={e => { e.target.style.borderColor = T.gold; }}
                  onBlur={e => { e.target.style.borderColor = hex2rgba(T.gold, 0.18); }}
                />
              </div>

              {/* Pills catégories */}
              {categories.length > 0 && (
                <div style={{
                  display: 'flex', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none',
                  padding: '12px 0 14px',
                  msOverflowStyle: 'none',
                }}>
                  {['', ...categories].map(cat => {
                    const active = filterCat === cat;
                    const count  = cat === '' ? products.length : products.filter(p => p.categorie === cat).length;
                    return (
                      <button
                        key={cat || '__all__'}
                        className="shop-cat-pill"
                        onClick={() => setFilterCat(cat)}
                        style={{
                          flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '7px 16px',
                          borderRadius: '100px',
                          border: active ? `1.5px solid ${T.gold}` : `1.5px solid ${hex2rgba(T.dark, 0.1)}`,
                          background: active ? hex2rgba(T.gold, 0.1) : 'transparent',
                          cursor: 'pointer', fontFamily: sans,
                          fontSize: '12px', fontWeight: active ? 800 : 600,
                          color: active ? T.gold : T.muted,
                          letterSpacing: active ? '0.01em' : 0,
                          boxShadow: active ? `0 0 0 3px ${hex2rgba(T.gold, 0.12)}` : 'none',
                        }}
                      >
                        {cat || 'Tout'}
                        <span style={{
                          fontSize: '10px', fontWeight: 700,
                          padding: '1px 6px', borderRadius: '100px',
                          background: active ? T.gold : hex2rgba(T.dark, 0.07),
                          color: active ? '#fff' : T.muted,
                        }}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {categories.length === 0 && <div style={{ height: '14px' }} />}
            </div>
          </div>

          {/* ── GRILLE PRODUITS ── */}
          <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 12px 100px' }}>

            {visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Package size={24} style={{ color: T.muted, marginBottom: '14px', opacity: 0.4 }} />
                <p style={{ fontFamily: sans, fontSize: '13px', fontWeight: 600, color: T.muted }}>Aucun produit trouvé</p>
              </div>
            ) : (
              <>
                {/* En-tête éditorial */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px', padding: '0 4px' }}>
                  <div style={{ height: '1px', width: '16px', background: T.gold, opacity: 0.7, flexShrink: 0 }} />
                  <span style={{ ...label, color: T.muted, flexShrink: 0 }}>
                    {filterCat ? filterCat : 'Collection'} · {visible.length} produit{visible.length > 1 ? 's' : ''}
                  </span>
                  <div style={{ flex: 1, height: '1px', background: T.subtle }} />
                </div>

                {/* Grille */}
                <div className="shop-grid">
                  {visible.map(p => (
                    <ProductCard
                      key={p._id}
                      product={p}
                      cartQty={cart.find(i => i.productId === p._id)?.quantite || 0}
                      onAdd={addToCart}
                      onDecrement={(id) => updateQty(id, -1)}
                      onOpen={setSelected}
                    />
                  ))}
                </div>
              </>
            )}
          </main>

          {/* ── STRIP CONTACT ── */}
          {salon?.telephone && (
            <div style={{ borderTop: `1px solid ${T.subtle}`, borderBottom: `1px solid ${T.subtle}`, margin: '0 24px 40px', padding: '20px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <span style={{ ...label, color: T.muted }}>Une question ?</span>
              <a href={`tel:${salon.telephone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: sans, fontWeight: 800, fontSize: '14px', color: T.dark, textDecoration: 'none' }}>
                <Phone size={13} style={{ color: T.gold }} /> {salon.telephone}
              </a>
            </div>
          )}

          {/* ── PANIER FLOTTANT mobile ── */}
          {cartCount > 0 && (
            <div className="sm:hidden" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40, padding: '0 16px', paddingBottom: 'max(16px, env(safe-area-inset-bottom))', background: `linear-gradient(to top, ${T.bg} 60%, transparent)`, paddingTop: '32px' }}>
              <button
                onClick={() => setShowCart(true)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderRadius: '16px', background: T.dark, border: 'none', cursor: 'pointer', boxShadow: '0 8px 40px rgba(14,13,11,0.4)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: T.gold, color: '#fff', fontSize: '10px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cartCount}</span>
                  <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '13px', color: '#fff' }}>Voir mon panier</span>
                </div>
                <span style={{ fontFamily: sans, fontWeight: 800, fontSize: '13px', color: T.gold }}>{fmt(total)} FCFA</span>
              </button>
            </div>
          )}
        </div>
      )}

      {view === 'checkout' && (
        <CheckoutView cart={cart} total={total} slug={slug} salon={salon}
          onBack={() => setView('shop')}
          onSuccess={() => { setLastTotal(total); setCart([]); setView('success'); }}
        />
      )}

      {view === 'success' && <SuccessView salon={salon} lastTotal={lastTotal} onBack={() => setView('shop')} />}

      {showCart && (
        <CartPanel
          cart={cart} total={total}
          onClose={() => setShowCart(false)}
          onUpdateQty={updateQty}
          onRemove={removeFromCart}
          onCheckout={() => { setShowCart(false); setView('checkout'); }}
        />
      )}

      {selectedProduct && (
        <ProductSheet
          product={selectedProduct}
          cartQty={cart.find(i => i.productId === selectedProduct._id)?.quantite || 0}
          onAdd={addToCart}
          onDecrement={(id) => updateQty(id, -1)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
    </ThemeContext.Provider>
  );
}

// ─── Carte produit ────────────────────────────────────────────────────────────

function ProductCard({ product, cartQty, onAdd, onDecrement, onOpen }) {
  const T      = React.useContext(ThemeContext);
  const oos    = product.stock === 0;
  const inCart = cartQty > 0;
  const [hov, setHov] = useState(false);
  const initial = (product.nom || '?').charAt(0).toUpperCase();

  return (
    <article
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => onOpen(product)}
      style={{
        borderRadius: '14px',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        /* Anneau gold quand dans le panier — signature visuelle */
        boxShadow: inCart
          ? `0 0 0 2.5px ${T.gold}, 0 16px 40px rgba(0,0,0,0.18)`
          : hov
            ? '0 20px 48px rgba(0,0,0,0.2)'
            : '0 3px 12px rgba(0,0,0,0.09)',
        transform: hov ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94)',
        opacity: oos ? 0.58 : 1,
      }}
    >
      {/* ── Image plein-cadre ── */}
      <div style={{ position: 'relative', paddingTop: '130%', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0 }}>

          {/* Photo ou placeholder éditorial */}
          {product.image ? (
            <img
              src={product.image}
              alt={product.nom}
              loading="lazy"
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transition: 'transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)',
                transform: hov ? 'scale(1.08)' : 'scale(1)',
              }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              background: `linear-gradient(160deg, ${T.beige} 0%, #C9C0B6 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: hex2rgba(T.gold, 0.13),
                border: `1.5px solid ${hex2rgba(T.gold, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontFamily: serif, fontSize: '28px', color: T.gold, fontStyle: 'italic' }}>{initial}</span>
              </div>
            </div>
          )}

          {/* Gradient overlay fort vers le bas — l'info vit dessus */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(6,4,2,0.94) 0%, rgba(6,4,2,0.55) 36%, rgba(6,4,2,0.08) 64%, transparent 100%)',
            pointerEvents: 'none',
          }} />
        </div>

        {/* ── Badges haut ── */}
        <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', zIndex: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
          {product.categorie ? (
            <span style={{
              fontFamily: sans, fontSize: '8px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
              background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(12px)',
              color: 'rgba(255,255,255,0.88)', padding: '4px 9px', borderRadius: '100px',
              border: '1px solid rgba(255,255,255,0.18)', flexShrink: 0,
            }}>
              {product.categorie}
            </span>
          ) : <span />}

          {!oos && product.stock <= 5 && (
            <span style={{
              fontFamily: sans, fontSize: '8px', fontWeight: 800,
              background: T.gold, color: '#fff', padding: '4px 9px', borderRadius: '100px', flexShrink: 0,
            }}>
              {product.stock} restant{product.stock > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* ── Info overlay bas ── */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3,
          padding: '14px 12px 12px',
        }}>
          {/* Nom */}
          <h3 style={{
            fontFamily: sans, fontWeight: 800, fontSize: '13px', color: '#fff',
            lineHeight: 1.35, margin: '0 0 2px',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {product.nom}
          </h3>

          {/* Voir les détails */}
          <p style={{
            fontFamily: sans, fontSize: '10px', fontStyle: 'italic',
            color: hex2rgba(T.gold, 0.85), margin: '0 0 8px', letterSpacing: '0.01em',
          }}>
            Voir les détails →
          </p>

          {/* Prix + action */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
              <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '16px', color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                {fmt(product.prix)}
              </span>
              <span style={{ fontFamily: sans, fontSize: '8px', fontWeight: 600, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em' }}>FCFA</span>
            </div>

            {/* Bouton ajout */}
            {!oos && !inCart && (
              <button
                onClick={e => { e.stopPropagation(); onAdd(product); }}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                  background: T.gold, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                  boxShadow: `0 4px 16px ${hex2rgba(T.gold, 0.55)}`,
                  transition: 'transform 0.2s ease',
                  transform: hov ? 'scale(1.14)' : 'scale(1)',
                }}
              >
                <Plus size={16} strokeWidth={2.5} />
              </button>
            )}

            {/* Stepper dans panier */}
            {!oos && inCart && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                  background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)',
                  borderRadius: '100px', padding: '3px',
                  border: `1px solid ${hex2rgba(T.gold, 0.4)}`,
                }}
              >
                <button onClick={() => onDecrement(product._id)}
                  style={{ width: '28px', height: '28px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)' }}>
                  <Minus size={10} strokeWidth={3} />
                </button>
                <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '13px', color: '#fff', minWidth: '16px', textAlign: 'center' }}>{cartQty}</span>
                <button onClick={() => onAdd(product)} disabled={cartQty >= product.stock}
                  style={{ width: '28px', height: '28px', background: cartQty >= product.stock ? 'transparent' : T.gold, border: 'none', borderRadius: '50%', cursor: cartQty >= product.stock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: cartQty >= product.stock ? 0.35 : 1, transition: 'background 0.2s' }}>
                  <Plus size={10} strokeWidth={3} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Overlay épuisé */}
        {oos && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(6,4,2,0.62)', backdropFilter: 'blur(4px)' }}>
            <span style={{ fontFamily: sans, fontSize: '8px', fontWeight: 800, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)', padding: '7px 18px', background: 'rgba(255,255,255,0.1)', borderRadius: '100px', border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }}>
              Épuisé
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

// ─── Fiche produit (drawer) ───────────────────────────────────────────────────

function ProductSheet({ product, cartQty, onAdd, onDecrement, onClose }) {
  const T      = React.useContext(ThemeContext);
  const oos    = product.stock === 0;
  const inCart = cartQty > 0;
  const initial = (product.nom || '?').charAt(0).toUpperCase();

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(6,4,2,0.72)', backdropFilter: 'blur(10px)' }} />

      <div style={{
        position: 'fixed', zIndex: 51,
        bottom: 0, left: 0, right: 0,
        maxHeight: '94vh',
        background: T.bg,
        borderRadius: '28px 28px 0 0',
        borderTop: `2px solid ${hex2rgba(T.gold, 0.3)}`,
        boxShadow: '0 -24px 80px rgba(6,4,2,0.3)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
        className="sm:bottom-0 sm:top-0 sm:left-auto sm:right-0 sm:w-[440px] sm:max-h-none sm:h-full sm:border-top-0 sm:rounded-none"
      >
        {/* Pill mobile */}
        <div className="sm:hidden" style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '2px', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '3px', borderRadius: '100px', background: hex2rgba(T.gold, 0.3) }} />
        </div>

        {/* Bouton fermer */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '18px', right: '18px', zIndex: 10,
          width: '36px', height: '36px', borderRadius: '50%',
          background: 'rgba(6,4,2,0.4)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.12)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <X size={14} style={{ color: '#fff' }} />
        </button>

        {/* Zone scrollable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* Image hero pleine largeur */}
          <div style={{ position: 'relative', paddingTop: '72%', overflow: 'hidden', flexShrink: 0, background: T.dark }}>
            <div style={{ position: 'absolute', inset: 0 }}>
              {product.image ? (
                <img src={product.image} alt={product.nom} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  background: `linear-gradient(145deg, ${T.dark} 0%, #2A2520 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontFamily: serif, fontSize: '80px', fontStyle: 'italic', color: hex2rgba(T.gold, 0.15), fontWeight: 400 }}>{initial}</span>
                </div>
              )}
              {/* Dégradé bas permettant au titre de flotter sur l'image */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: `linear-gradient(to top, ${T.dark} 0%, ${hex2rgba(T.dark, 0.7)} 50%, transparent 100%)`, pointerEvents: 'none' }} />
            </div>

            {/* Badges en haut */}
            <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {product.categorie && (
                <span style={{ ...label, background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(14px)', color: 'rgba(255,255,255,0.9)', padding: '5px 12px', borderRadius: '100px', border: '1px solid rgba(255,255,255,0.18)' }}>
                  {product.categorie}
                </span>
              )}
              {!oos && product.stock <= 5 && (
                <span style={{ fontFamily: sans, fontSize: '8px', fontWeight: 800, background: T.gold, color: '#fff', padding: '5px 12px', borderRadius: '100px', letterSpacing: '0.04em' }}>
                  {product.stock} restant{product.stock > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Nom + prix flottant sur l'image */}
            <div style={{ position: 'absolute', bottom: '20px', left: '20px', right: '20px' }}>
              <h2 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(20px, 5vw, 26px)', color: '#fff', lineHeight: 1.2, margin: '0 0 8px', textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>
                {product.nom}
              </h2>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '22px', color: T.gold, fontVariantNumeric: 'tabular-nums' }}>{fmt(product.prix)}</span>
                <span style={{ fontFamily: sans, fontSize: '10px', fontWeight: 700, color: hex2rgba(T.gold, 0.7), letterSpacing: '0.08em' }}>FCFA</span>
              </div>
            </div>
          </div>

          {/* Corps texte */}
          <div style={{ padding: '24px 22px 16px' }}>
            {/* Séparateur gold */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '20px', height: '1px', background: T.gold, opacity: 0.5 }} />
              <span style={{ ...label, color: T.gold, opacity: 0.7 }}>Description</span>
            </div>

            {product.description ? (
              <p style={{ fontFamily: sans, fontSize: '14px', color: T.muted, lineHeight: 1.85, margin: 0 }}>
                {product.description}
              </p>
            ) : (
              <p style={{ fontFamily: sans, fontSize: '13px', color: hex2rgba(T.dark, 0.25), fontStyle: 'italic', margin: 0 }}>
                Aucune description disponible pour ce produit.
              </p>
            )}

            {oos && (
              <div style={{ marginTop: '24px', padding: '14px 18px', borderRadius: '14px', background: T.beige, textAlign: 'center', fontFamily: sans, fontWeight: 700, fontSize: '12px', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Produit épuisé
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        {!oos && (
          <div style={{
            padding: '18px 20px', paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
            borderTop: `1px solid ${hex2rgba(T.gold, 0.14)}`,
            flexShrink: 0, background: T.bg,
          }}>
            {inCart ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Stepper premium */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  border: `1.5px solid ${hex2rgba(T.gold, 0.3)}`,
                  borderRadius: '14px', padding: '6px 10px',
                  background: hex2rgba(T.gold, 0.05), flexShrink: 0,
                }}>
                  <button onClick={() => onDecrement(product._id)} style={{ width: '36px', height: '36px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.dark }}>
                    <Minus size={14} strokeWidth={2.5} />
                  </button>
                  <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '16px', color: T.gold, width: '28px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{cartQty}</span>
                  <button onClick={() => onAdd(product)} disabled={cartQty >= product.stock} style={{ width: '36px', height: '36px', background: 'none', border: 'none', cursor: cartQty >= product.stock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gold, opacity: cartQty >= product.stock ? 0.3 : 1 }}>
                    <Plus size={14} strokeWidth={2.5} />
                  </button>
                </div>
                <button onClick={onClose} style={{ flex: 1, padding: '14px', borderRadius: '14px', background: T.dark, border: 'none', cursor: 'pointer', fontFamily: sans, fontWeight: 800, fontSize: '13px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', letterSpacing: '0.01em' }}>
                  <ShoppingBag size={14} /> Voir mon panier
                </button>
              </div>
            ) : (
              <button onClick={() => onAdd(product)} style={{
                width: '100%', padding: '16px', borderRadius: '14px',
                background: T.gold, border: 'none', cursor: 'pointer',
                fontFamily: sans, fontWeight: 800, fontSize: '14px', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                boxShadow: `0 8px 32px ${hex2rgba(T.gold, 0.4)}`,
                letterSpacing: '0.01em',
              }}>
                <Plus size={16} strokeWidth={2.5} /> Ajouter au panier
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Panneau panier ───────────────────────────────────────────────────────────

function CartPanel({ cart, total, onClose, onUpdateQty, onRemove, onCheckout }) {
  const T     = React.useContext(ThemeContext);
  const count = cart.reduce((s, i) => s + i.quantite, 0);

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(6,4,2,0.68)', backdropFilter: 'blur(10px)' }} onClick={onClose} />
      <div style={{
        position: 'fixed', zIndex: 51,
        bottom: 0, left: 0, right: 0,
        maxHeight: '92vh',
        background: T.bg,
        borderRadius: '28px 28px 0 0',
        borderTop: `2px solid ${hex2rgba(T.gold, 0.25)}`,
        boxShadow: '0 -24px 80px rgba(6,4,2,0.25)',
        display: 'flex', flexDirection: 'column',
      }} className="sm:top-0 sm:bottom-0 sm:left-auto sm:right-0 sm:w-[420px] sm:max-h-none sm:h-full sm:rounded-none">

        {/* Pill mobile */}
        <div className="sm:hidden" style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '2px', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '3px', borderRadius: '100px', background: hex2rgba(T.gold, 0.3) }} />
        </div>

        {/* Header */}
        <div style={{ padding: '18px 22px 16px', borderBottom: `1px solid ${hex2rgba(T.gold, 0.1)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '22px', color: T.dark, margin: 0, lineHeight: 1 }}>Mon panier</h2>
            <p style={{ ...label, color: T.gold, marginTop: '5px' }}>{count} article{count > 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} style={{ width: '38px', height: '38px', borderRadius: '50%', background: T.beige, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={15} style={{ color: T.dark }} />
          </button>
        </div>

        {/* Liste des articles */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {cart.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '60px 0', gap: '14px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: hex2rgba(T.gold, 0.08), border: `1px solid ${hex2rgba(T.gold, 0.18)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingBag size={22} style={{ color: T.gold }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontFamily: sans, fontWeight: 700, fontSize: '15px', color: T.dark, margin: 0 }}>Votre panier est vide</p>
                <p style={{ fontFamily: sans, fontSize: '12px', color: T.muted, margin: '4px 0 0' }}>Ajoutez des produits depuis la boutique</p>
              </div>
            </div>
          ) : cart.map(item => (
            <div key={item.productId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '16px', background: T.card, border: `1px solid ${hex2rgba(T.gold, 0.08)}` }}>
              {item.image
                ? <img src={item.image} alt={item.nom} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0 }} />
                : <div style={{ width: '56px', height: '56px', borderRadius: '10px', background: `linear-gradient(145deg, ${T.beige}, #DDD7CE)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Package size={18} style={{ color: T.muted }} />
                  </div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: sans, fontWeight: 700, fontSize: '13px', color: T.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{item.nom}</p>
                <p style={{ fontFamily: sans, fontWeight: 900, fontSize: '14px', color: T.gold, marginTop: '2px', fontVariantNumeric: 'tabular-nums', margin: '2px 0 0' }}>
                  {fmt(item.prix)} <span style={{ fontSize: '9px', color: T.muted, fontWeight: 600 }}>FCFA</span>
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => onRemove(item.productId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', opacity: 0.45 }}>
                  <Trash2 size={13} style={{ color: T.dark }} />
                </button>
                <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${hex2rgba(T.gold, 0.25)}`, borderRadius: '100px', overflow: 'hidden' }}>
                  <button onClick={() => onUpdateQty(item.productId, -1)} style={{ width: '28px', height: '26px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted }}>
                    <Minus size={10} strokeWidth={2.5} />
                  </button>
                  <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '12px', color: T.dark, width: '18px', textAlign: 'center' }}>{item.quantite}</span>
                  <button onClick={() => item.quantite < item.stock && onUpdateQty(item.productId, 1)} disabled={item.quantite >= item.stock} style={{ width: '28px', height: '26px', background: item.quantite < item.stock ? T.gold : 'none', border: 'none', cursor: item.quantite >= item.stock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.quantite < item.stock ? '#fff' : T.muted, opacity: item.quantite >= item.stock ? 0.3 : 1 }}>
                    <Plus size={10} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer total + commander */}
        {cart.length > 0 && (
          <div style={{ padding: '18px 20px', paddingBottom: 'max(22px, env(safe-area-inset-bottom))', borderTop: `1px solid ${hex2rgba(T.gold, 0.12)}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontFamily: sans, fontSize: '12px', color: T.muted, fontWeight: 600 }}>Total estimé</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '26px', color: T.gold, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{fmt(total)}</span>
                <span style={{ fontFamily: sans, fontSize: '10px', color: T.muted, display: 'block', marginTop: '1px', letterSpacing: '0.06em' }}>FCFA</span>
              </div>
            </div>
            <button
              onClick={onCheckout}
              style={{
                width: '100%', padding: '16px', borderRadius: '16px',
                background: T.gold, border: 'none', cursor: 'pointer',
                fontFamily: sans, fontWeight: 800, fontSize: '14px', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                letterSpacing: '0.02em', boxShadow: `0 8px 32px ${hex2rgba(T.gold, 0.4)}`,
              }}
            >
              Commander <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Vue Checkout ─────────────────────────────────────────────────────────────

function CheckoutView({ cart, total, slug, salon, onBack, onSuccess }) {
  const T = React.useContext(ThemeContext);
  const [form, setForm]           = useState({ prenom: '', nom: '', email: '', telephone: '', adresse: '', note: '' });
  const [submitting, setSubmit]   = useState(false);
  const [error, setError]         = useState('');
  const [showRecap, setShowRecap] = useState(false);
  const set = (f) => (e) => {
    const v = f === 'telephone' ? filterPhone(e.target.value) : e.target.value;
    setForm(p => ({ ...p, [f]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmit(true);
    if (!isValidEmail(form.email)) {
      setError('Adresse email invalide. Vérifiez le format (ex : nom@domaine.com).');
      setSubmit(false); return;
    }
    try {
      await axios.post(`${BASE_URL}/api/shop/${slug}/order`, {
        client: { nom: form.nom, prenom: form.prenom, email: form.email, telephone: form.telephone, adresse: form.adresse },
        items: cart.map(i => ({ productId: i.productId, quantite: i.quantite })),
        note: form.note,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur. Veuillez réessayer.');
    } finally { setSubmit(false); }
  };

  const nbArticles = cart.reduce((s, i) => s + i.quantite, 0);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, paddingTop: '60px', fontFamily: sans }}>
      {/* Barre de navigation du checkout */}
      <div style={{
        position: 'sticky', top: '60px', zIndex: 30,
        background: 'rgba(250,250,248,0.97)', backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${hex2rgba(T.gold, 0.12)}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: '14px',
      }}>
        <button onClick={onBack} style={{ width: '36px', height: '36px', borderRadius: '50%', background: T.beige, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ArrowLeft size={16} style={{ color: T.dark }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '18px', color: T.dark, margin: 0, lineHeight: 1 }}>Finaliser la commande</h2>
          <p style={{ ...label, color: T.gold, marginTop: '4px' }}>
            {nbArticles} article{nbArticles > 1 ? 's' : ''} · {fmt(total)} FCFA
          </p>
        </div>
        {salon?.nom && (
          <span style={{ fontFamily: sans, fontSize: '11px', fontWeight: 700, color: T.muted, display: 'none' }} className="sm:block">{salon.nom}</span>
        )}
      </div>

      {/* Layout desktop : 2 colonnes */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 16px 64px', display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }} className="lg:grid-cols-checkout">
        <style>{`.lg\\:grid-cols-checkout { grid-template-columns: 1fr 380px !important; } @media (max-width: 1023px) { .lg\\:grid-cols-checkout { grid-template-columns: 1fr !important; } }`}</style>

        {/* Colonne gauche : formulaire */}
        <div>
          {/* Bannière info paiement */}
          <div style={{ display: 'flex', gap: '14px', padding: '14px 16px', borderRadius: '16px', background: hex2rgba(T.gold, 0.08), border: `1.5px solid ${hex2rgba(T.gold, 0.2)}`, marginBottom: '24px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: T.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Phone size={14} style={{ color: '#fff' }} />
            </div>
            <div>
              <p style={{ fontFamily: sans, fontWeight: 800, fontSize: '13px', color: T.dark, margin: '0 0 3px' }}>Commandez maintenant, payez après</p>
              <p style={{ fontFamily: sans, fontSize: '12px', color: T.muted, lineHeight: 1.65, margin: 0 }}>Notre équipe vous contactera pour organiser le paiement et la livraison.</p>
            </div>
          </div>

          {/* Formulaire */}
          <div style={{ borderRadius: '20px', background: T.card, border: `1px solid ${hex2rgba(T.gold, 0.1)}`, overflow: 'hidden' }}>
            {/* Header section */}
            <div style={{ padding: '18px 20px 0', borderBottom: `1px solid ${hex2rgba(T.gold, 0.08)}`, paddingBottom: '14px' }}>
              <h3 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '20px', color: T.dark, margin: 0 }}>Vos coordonnées</h3>
              <p style={{ fontFamily: sans, fontSize: '12px', color: T.muted, margin: '4px 0 0' }}>Pour la confirmation et l'organisation de la livraison</p>
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 20px', background: '#FEF2F2', borderBottom: '1px solid #FEE2E2' }}>
                <AlertCircle size={14} style={{ color: '#EF4444', flexShrink: 0, marginTop: '1px' }} />
                <p style={{ fontFamily: sans, fontSize: '13px', color: '#B91C1C', fontWeight: 500, margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <CField label="Prénom *" type="text"  value={form.prenom}    onChange={set('prenom')}    placeholder={getPlaceholders(salon?.devise).prenom} required />
                <CField label="Nom *"    type="text"  value={form.nom}       onChange={set('nom')}       placeholder={getPlaceholders(salon?.devise).nom}    required />
              </div>
              <CField label="Email *"                type="email" value={form.email}     onChange={set('email')}     placeholder={getPlaceholders(salon?.devise).email}     required />
              <CField label="Téléphone *"            type="tel"   value={form.telephone} onChange={set('telephone')} placeholder={getPlaceholders(salon?.devise).telephone}  required />
              <CField label="Adresse de livraison *" type="text"  value={form.adresse}   onChange={set('adresse')}   placeholder={getPlaceholders(salon?.devise).adresse}   required />
              <div>
                <label style={{ ...label, color: T.muted, display: 'block', marginBottom: '6px' }}>Note (optionnel)</label>
                <textarea
                  value={form.note} onChange={set('note')} placeholder="Instructions particulières…"
                  style={{ width: '100%', padding: '12px 14px', fontFamily: sans, fontSize: '13px', color: T.dark, background: T.beige, border: `1.5px solid ${hex2rgba(T.gold, 0.15)}`, borderRadius: '12px', outline: 'none', resize: 'none', height: '80px', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                  onFocus={e => { e.target.style.borderColor = T.gold; }}
                  onBlur={e => { e.target.style.borderColor = hex2rgba(T.gold, 0.15); }}
                />
              </div>

              <button type="submit" disabled={submitting} style={{
                width: '100%', padding: '16px', borderRadius: '14px',
                background: submitting ? T.muted : T.gold,
                border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: sans, fontWeight: 800, fontSize: '14px', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                boxShadow: submitting ? 'none' : `0 8px 32px ${hex2rgba(T.gold, 0.4)}`,
                letterSpacing: '0.02em', transition: 'all 0.2s',
              }}>
                {submitting
                  ? <><Loader2 size={15} className="animate-spin" /> Envoi en cours…</>
                  : <>Confirmer ma commande <ArrowRight size={15} /></>}
              </button>
            </form>
          </div>

          {/* Lien téléphone */}
          {salon?.telephone && (
            <a href={`tel:${salon.telephone}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '14px', background: T.card, border: `1px solid ${hex2rgba(T.gold, 0.1)}`, textDecoration: 'none', marginTop: '12px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: hex2rgba(T.gold, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Phone size={14} style={{ color: T.gold }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: sans, fontSize: '10px', color: T.muted, margin: 0, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700 }}>Une question ?</p>
                <p style={{ fontFamily: sans, fontWeight: 800, fontSize: '14px', color: T.dark, margin: '2px 0 0' }}>{salon.telephone}</p>
              </div>
              <ArrowRight size={14} style={{ color: T.muted }} />
            </a>
          )}
        </div>

        {/* Colonne droite : récap commande */}
        <div>
          <div style={{ borderRadius: '20px', background: T.dark, overflow: 'hidden', position: 'sticky', top: '130px' }}>
            {/* Header récap */}
            <div style={{ padding: '18px 20px', borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
              <h3 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '18px', color: '#fff', margin: 0 }}>Récapitulatif</h3>
              <p style={{ ...label, color: T.gold, marginTop: '4px' }}>{nbArticles} article{nbArticles > 1 ? 's' : ''}</p>
            </div>

            {/* Mobile : accordion */}
            <button onClick={() => setShowRecap(!showRecap)} className="lg:hidden" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Voir le détail</span>
              <ChevronDown size={13} style={{ color: 'rgba(255,255,255,0.4)', transform: showRecap ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </button>

            {/* Items */}
            <div className={`${showRecap ? '' : 'hidden'} lg:block`} style={{ padding: '4px 20px 0' }}>
              {cart.map(item => (
                <div key={item.productId} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
                  {item.image
                    ? <img src={item.image} alt={item.nom} style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                    : <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: sans, fontWeight: 700, fontSize: '12px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{item.nom}</p>
                    <p style={{ fontFamily: sans, fontSize: '11px', color: 'rgba(255,255,255,0.35)', margin: '2px 0 0' }}>× {item.quantite}</p>
                  </div>
                  <p style={{ fontFamily: sans, fontWeight: 800, fontSize: '13px', color: T.gold, flexShrink: 0, fontVariantNumeric: 'tabular-nums', margin: 0 }}>{fmt(item.prix * item.quantite)}</p>
                </div>
              ))}
            </div>

            {/* Total */}
            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <span style={{ fontFamily: sans, fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>Total estimé</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontFamily: sans, fontWeight: 900, fontSize: '24px', color: T.gold, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{fmt(total)}</span>
                <p style={{ fontFamily: sans, fontSize: '9px', color: 'rgba(255,255,255,0.3)', margin: '2px 0 0', letterSpacing: '0.08em', textTransform: 'uppercase' }}>FCFA</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CField({ label: lbl, ...props }) {
  const T = React.useContext(ThemeContext);
  return (
    <div>
      <label style={{ ...label, color: T.muted, display: 'block', marginBottom: '6px' }}>{lbl}</label>
      <input
        {...props}
        style={{ width: '100%', padding: '12px 14px', fontFamily: sans, fontSize: '13px', color: T.dark, background: T.beige, border: `1.5px solid transparent`, borderRadius: '10px', outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box' }}
        onFocus={e => { e.target.style.borderColor = T.gold; e.target.style.background = T.card; }}
        onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = T.beige; }}
      />
    </div>
  );
}

// ─── Vue Succès ───────────────────────────────────────────────────────────────

function SuccessView({ salon, lastTotal, onBack }) {
  const T = React.useContext(ThemeContext);

  const paymentUrl = React.useMemo(() => {
    if (!salon?.shopPaymentLink) return null;
    return salon.shopPaymentLink.replace('{montant}', lastTotal ?? '');
  }, [salon?.shopPaymentLink, lastTotal]);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', fontFamily: sans }}>
      <div style={{ width: '100%', maxWidth: '360px', textAlign: 'center' }}>
        <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 36px' }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: T.goldBg, border: `1px solid ${T.goldBd}` }} />
          <CheckCircle size={40} strokeWidth={1.5} style={{ color: T.gold, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
        </div>

        <p style={{ ...label, color: T.gold, marginBottom: '12px' }}>Commande enregistrée</p>
        <h2 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '2.2rem', color: T.dark, letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: '16px' }}>
          Merci pour votre commande
        </h2>
        <p style={{ fontSize: '14px', color: T.muted, lineHeight: 1.7, marginBottom: '36px' }}>
          Votre commande chez <strong style={{ color: T.dark, fontWeight: 700 }}>{salon?.nom}</strong> a bien été enregistrée. Un email de confirmation vous a été envoyé.
        </p>

        {/* ── CTA Paiement immédiat ── */}
        {paymentUrl && (
          <div style={{
            marginBottom: '24px',
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${hex2rgba(T.gold, 0.12)} 0%, ${hex2rgba(T.gold, 0.06)} 100%)`,
            border: `1.5px solid ${T.goldBd}`,
            padding: '20px',
            textAlign: 'left',
          }}>
            {/* Accroche */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '18px' }}>⚡</span>
              <p style={{ fontFamily: sans, fontWeight: 800, fontSize: '13px', color: T.dark, margin: 0 }}>
                Payez maintenant, soyez livré en priorité
              </p>
            </div>
            <p style={{ fontFamily: sans, fontSize: '12px', color: T.muted, lineHeight: 1.6, marginBottom: '16px' }}>
              Les commandes réglées en ligne sont traitées <strong style={{ color: T.dark }}>en premier</strong>. Profitez-en - votre panier est réservé.
            </p>

            {/* Bouton principal */}
            <a href={paymentUrl} target="_blank" rel="noreferrer" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              padding: '17px 20px', borderRadius: '12px', textDecoration: 'none',
              fontFamily: sans, fontWeight: 800, fontSize: '15px', color: '#fff',
              background: T.gold,
              boxShadow: `0 10px 36px ${hex2rgba(T.gold, 0.42)}`,
              marginBottom: '10px',
            }}>
              💳 Payer {fmt(lastTotal)} FCFA
            </a>

            {/* Réassurance */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              {['🔒 Sécurisé', '⚡ Prioritaire', '✅ Confirmé'].map(tag => (
                <span key={tag} style={{ fontFamily: sans, fontSize: '10px', fontWeight: 700, color: T.muted, letterSpacing: '0.02em' }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Infos suivi ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
          {[
            { icon: '📧', text: 'Vous recevrez un email à chaque mise à jour de votre commande.' },
            { icon: '📞', text: paymentUrl ? 'Notre équipe vous contactera pour confirmer la livraison.' : 'Notre équipe vous appellera pour le paiement et la livraison.' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: T.card, border: `1px solid ${T.subtle}`, textAlign: 'left' }}>
              <span style={{ fontSize: '14px', lineHeight: 1.4, flexShrink: 0 }}>{s.icon}</span>
              <p style={{ fontFamily: sans, fontSize: '12px', color: T.muted, lineHeight: 1.6, margin: 0 }}>{s.text}</p>
            </div>
          ))}
        </div>

        {/* ── Actions secondaires ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {salon?.telephone && (
            <a href={`tel:${salon.telephone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '12px', fontFamily: sans, fontWeight: 700, fontSize: '13px', color: T.gold, border: `1px solid ${T.goldBd}`, background: T.goldBg, textDecoration: 'none' }}>
              <Phone size={13} /> Appeler {salon.nom}
            </a>
          )}
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '12px', fontFamily: sans, fontWeight: 700, fontSize: '13px', color: T.muted, background: 'transparent', border: `1px solid ${T.subtle}`, cursor: 'pointer' }}>
            <ArrowLeft size={13} /> Retour à la boutique
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Écrans d'état ────────────────────────────────────────────────────────────

function Loader() {
  const T = React.useContext(ThemeContext);
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', fontFamily: sans }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: `1.5px solid rgba(176,141,87,0.2)`, borderTopColor: T.gold, animation: 'spin 0.9s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase', color: T.muted }}>Chargement</p>
    </div>
  );
}

function ErrScreen({ msg }) {
  const T = React.useContext(ThemeContext);
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: sans }}>
      <div style={{ textAlign: 'center', maxWidth: '300px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Package size={24} style={{ color: '#FCA5A5' }} />
        </div>
        <h2 style={{ fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '1.6rem', color: T.dark, marginBottom: '8px' }}>Boutique introuvable</h2>
        <p style={{ fontSize: '13px', color: T.muted }}>{msg}</p>
      </div>
    </div>
  );
}
